import React, { useEffect, useMemo, useState } from "react";
import { Navigation } from "../components/navigation";
import { Footer } from "../components/footer";
import "./donationHistory/donationHistory.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ?? "";
const buildApiUrl = (endpoint) => (API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint);

const donorTypeLabels = {
  Meal_Donor: "ဆွမ်းအဟာရ အလှူရှင်များ",
  Drink_Donor: "ညနေခင်းဖျော်ရည် အလှူရှင်များ",
  General_Donor: "အဆောက်အဦအလှူရှင်များ",
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(parsed);
};

const getDonorName = (donation) =>
  donation?.donor_name ??
  donation?.donor?.donor_name ??
  donation?.donor?.name ??
  donation?.donor_name_mm ??
  (donation?.donor_id ? `Donor #${donation.donor_id}` : "Anonymous");

const DonationHistoryPage = () => {
  const [donations, setDonations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadDonations = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(buildApiUrl("/donations"), {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load donations (${response.status})`);
        }
        const data = await response.json();
        const normalized = Array.isArray(data) ? data : data?.data ?? [];
        setDonations(normalized);
      } catch (error) {
        if (error.name === "AbortError") return;
        setErrorMessage("Unable to load donation history right now.");
        setDonations([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDonations();

    return () => controller.abort();
  }, []);

  const rows = useMemo(
    () =>
      donations.map((donation, index) => {
        const donorType =
          donorTypeLabels[donation.donor_type] ?? donation.donor_type ?? "-";
        const amount = donation.amount != null ? `MMK ${donation.amount}` : "";
        const descriptionParts = [
          donation.description,
          amount,
        ].filter(Boolean);

        return {
          key: donation.id ?? `${donation.donor_id ?? "donor"}-${index}`,
          date: formatDate(donation.date),
          type: donorType,
          name: getDonorName(donation),
          description: descriptionParts.join(" • ") || "-",
        };
      }),
    [donations]
  );

  return (
    <>
      <Navigation />
      <main className="donation-history-page">
        <section className="donation-history-hero">
          <h1 className="donation-history-title"> အလှူမှတ်တမ်း</h1>
          <p className="donation-history-description">
            These records highlight recent support from donors who help sustain our monastery.
          </p>
        </section>

        <section className="donation-history-list">
          <header className="donation-history-list__header">
            <div>
              <p className="donation-history-list__label">
                ဆွမ်းအဟာရ အလှူရှင်များ
              </p>
              <h2 className="donation-history-list__title">
                {rows.length} recorded contributions
              </h2>
            </div>
          </header>

          <div className="donation-history-table-wrapper">
            {isLoading && <div>Loading donation history...</div>}
            {!isLoading && errorMessage && <div>{errorMessage}</div>}
            {!isLoading && !errorMessage && rows.length === 0 && (
              <div>No donation records found.</div>
            )}
            {!isLoading && !errorMessage && rows.length > 0 && (
              <table className="donation-history-table">
                <thead>
                  <tr>
                    <th>ရက်စွဲ</th>
                    <th>အမျိုးအစား</th>
                    <th>အမည်</th>
                    <th>မှတ်ချက်</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((donation) => (
                    <tr key={donation.key}>
                      <td>{donation.date}</td>
                      <td>{donation.type}</td>
                      <td>{donation.name}</td>
                      <td>{donation.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DonationHistoryPage;
